/**
 * GRI TNT Full Setup cart guards
 *
 * Two rules enforced sitewide:
 *   1. The Sydney Setup Distance Surcharge line item cannot be removed and its
 *      quantity cannot be edited by the customer. It is driven entirely by the
 *      distance from HQ picked on the TNT booking page.
 *   2. If the customer removes the parent TNT Gender Reveal Full Setup line item
 *      from cart, the linked Distance Surcharge line item is auto removed too
 *      so no one is billed a surcharge with nothing to attach it to.
 *
 * The UI lock uses a MutationObserver to catch drawer and cart-page rerenders
 * from Dawn's <cart-drawer> AND from third-party cart apps (Corner App). The
 * auto-remove logic wraps window.fetch so we react to /cart/change and
 * /cart/update mutations regardless of who initiated them.
 */
(function () {
  'use strict';

  // Variant IDs — must match sections/tnt-booking-page.liquid
  var FULL_SETUP_VARIANTS = [44652739657817, 44652739690585, 44652739723353];
  var DISTANCE_SURCHARGE_VARIANT = 44652878790745;
  var SURCHARGE_TITLE_MATCH = /sydney setup distance surcharge/i;

  // ────────────────────────────────────────────────────────────────────────
  // 1. UI LOCK — disable remove and quantity controls on the surcharge line
  // ────────────────────────────────────────────────────────────────────────
  function isSurchargeElement(el) {
    if (!el || !el.getAttribute) return false;
    // Check data attribute first (Dawn theme)
    var vid = el.getAttribute('data-variant-id');
    if (vid && parseInt(vid, 10) === DISTANCE_SURCHARGE_VARIANT) return true;
    // Fall back to text content for third-party drawers (Corner App)
    var txt = (el.textContent || '').trim();
    if (txt && SURCHARGE_TITLE_MATCH.test(txt)) return true;
    return false;
  }

  function findSurchargeLines() {
    var lines = [];
    // Dawn cart drawer / cart page: cart-remove-button carries data-variant-id
    var buttons = document.querySelectorAll('cart-remove-button[data-variant-id]');
    buttons.forEach(function (btn) {
      if (parseInt(btn.getAttribute('data-variant-id'), 10) === DISTANCE_SURCHARGE_VARIANT) {
        // Walk up to the nearest row / list item
        var row = btn.closest('tr, .cart-item, li, .cart__row, [class*="cart-item"], [class*="drawer-item"]') || btn.parentElement;
        if (row) lines.push({ row: row, source: 'dawn' });
      }
    });
    // Corner App / generic drawers: match by product title text
    var candidateRows = document.querySelectorAll('[class*="cart"] li, [class*="drawer"] li, [class*="line-item"], [class*="lineitem"], [data-cart-item]');
    candidateRows.forEach(function (row) {
      if (SURCHARGE_TITLE_MATCH.test(row.textContent || '')) {
        lines.push({ row: row, source: 'generic' });
      }
    });
    return lines;
  }

  function lockLine(row) {
    if (!row || row.dataset.griSurchargeLocked === '1') return;
    row.dataset.griSurchargeLocked = '1';
    row.classList.add('gri-surcharge-locked');
    // Remove buttons: Dawn's cart-remove-button custom element + generic remove links/buttons
    var removeTargets = row.querySelectorAll(
      'cart-remove-button, .cart-remove-button, .cart-item__remove, .cart__remove, ' +
      '[aria-label*="Remove" i], [aria-label*="Delete" i], ' +
      'a[href*="/cart/change?"], button[data-action="remove"], .remove-item'
    );
    removeTargets.forEach(function (el) {
      el.style.pointerEvents = 'none';
      el.style.opacity = '0';
      el.style.visibility = 'hidden';
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute('tabindex', '-1');
    });
    // Quantity controls
    var qtyTargets = row.querySelectorAll(
      'quantity-input, .quantity, .cart-quantity, ' +
      'input[type="number"], .qty, .quantity-selector, [class*="quantity"]'
    );
    qtyTargets.forEach(function (el) {
      el.style.pointerEvents = 'none';
      el.style.opacity = '0.55';
      var inputs = el.querySelectorAll('input, button');
      inputs.forEach(function (i) {
        i.disabled = true;
        i.setAttribute('aria-disabled', 'true');
        i.setAttribute('tabindex', '-1');
        if (i.tagName === 'INPUT') { i.readOnly = true; }
      });
    });
  }

  function scanAndLock() {
    var lines = findSurchargeLines();
    lines.forEach(function (info) { lockLine(info.row); });
  }

  // ────────────────────────────────────────────────────────────────────────
  // 2. AUTO REMOVE — if TNT Full Setup leaves the cart, drop the surcharge too
  // ────────────────────────────────────────────────────────────────────────
  var syncInFlight = false;
  function ensureConsistency() {
    if (syncInFlight) return Promise.resolve();
    syncInFlight = true;
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        syncInFlight = false;
        if (!cart || !cart.items) return;
        var hasFullSetup = cart.items.some(function (item) {
          return FULL_SETUP_VARIANTS.indexOf(item.variant_id) !== -1;
        });
        var surcharge = cart.items.filter(function (item) {
          return item.variant_id === DISTANCE_SURCHARGE_VARIANT;
        });
        if (!hasFullSetup && surcharge.length > 0) {
          // Remove every orphaned surcharge line by key
          var chain = Promise.resolve();
          surcharge.forEach(function (item) {
            chain = chain.then(function () {
              return fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                credentials: 'same-origin',
                body: JSON.stringify({ id: item.key, quantity: 0 })
              });
            });
          });
          return chain.then(function () {
            // Nudge every cart UI to refresh
            try { document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); } catch (e) {}
            try { document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true })); } catch (e) {}
            try { document.documentElement.dispatchEvent(new CustomEvent('cart-update', { bubbles: true, detail: { source: 'gri-cart-guard' } })); } catch (e) {}
            try {
              if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
                window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'gri-cart-guard' });
              }
            } catch (e) {}
          });
        }
      })
      .catch(function () { syncInFlight = false; });
  }

  // Intercept cart mutations so the consistency check runs afterwards
  var originalFetch = window.fetch;
  var cartMutationRe = /\/cart\/(change|update|add|clear)(\.js)?(\?|$)/i;
  window.fetch = function () {
    var url = arguments[0];
    var urlStr = typeof url === 'string' ? url : (url && url.url ? url.url : '');
    var p = originalFetch.apply(window, arguments);
    if (urlStr && cartMutationRe.test(urlStr)) {
      p.then(function () { setTimeout(ensureConsistency, 120); });
    }
    return p;
  };

  // ────────────────────────────────────────────────────────────────────────
  // Boot
  // ────────────────────────────────────────────────────────────────────────
  var observer = null;
  function boot() {
    scanAndLock();
    if (!observer) {
      observer = new MutationObserver(function () { scanAndLock(); });
      observer.observe(document.body, { childList: true, subtree: true });
    }
    // Initial consistency pass in case the cart already contains an orphaned surcharge
    ensureConsistency();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
